/**
 * Finished rows of docs/3-system/PRODUCT_ROADMAP.md kept one per file.
 *
 * A row for an item finished from 2026-10-29 on is its own file,
 * docs/3-system/roadmap-finished/<ID>.md, holding that one row. Every pull
 * request that finished an item used to add its row at the bottom of the same
 * table, so any two of them collided on the same lines: on 2026-10-28 that held
 * up seven pull requests at once, and each merge broke the rest again. A file
 * per item is written by one pull request only. The rows already in the table
 * stay there; `withFinishedFiles` puts the files' rows after them, so the page
 * (/home/roadmap) and guard:roadmap-finished read one table.
 */

const FINISHED_HEADING = /^## Finished\s*$/m;
export const ROADMAP_FINISHED_DIR = 'docs/3-system/roadmap-finished';
const FINISHED_FILE_NAME = /^([A-Z]{1,3}-\d+)\.md$/;

export interface FinishedFile {
  /** The file's name inside ROADMAP_FINISHED_DIR, e.g. "S-6.md". */
  name: string;
  text: string;
}

/** Only files named for an ID are rows; a README or anything else in the folder is not. */
export function isFinishedFileName(name: string): boolean {
  return FINISHED_FILE_NAME.test(name);
}

function rowLines(text: string): string[] {
  return text.split('\n').map((l) => l.trim()).filter((l) => l.startsWith('|'));
}

/**
 * The roadmap with each finished file's row added to the end of the Finished
 * table, files in ID order. Files not named for an ID are left out.
 */
export function withFinishedFiles(markdown: string, files: FinishedFile[]): string {
  const rows = files
    .filter((f) => isFinishedFileName(f.name))
    .sort((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true }))
    .flatMap((f) => rowLines(f.text));
  if (rows.length === 0) return markdown;
  const heading = FINISHED_HEADING.exec(markdown);
  if (!heading) return `${markdown.trimEnd()}\n\n## Finished\n\n| ID | What it was | Finished in | Merged |\n|---|---|---|---|\n${rows.join('\n')}\n`;
  const lines = markdown.split('\n');
  const headingLine = markdown.slice(0, heading.index).split('\n').length - 1;
  let last = -1;
  for (let i = headingLine; i < lines.length; i++) if (lines[i].startsWith('|')) last = i;
  if (last === -1) return `${markdown.trimEnd()}\n\n| ID | What it was | Finished in | Merged |\n|---|---|---|---|\n${rows.join('\n')}\n`;
  return [...lines.slice(0, last + 1), ...rows, ...lines.slice(last + 1)].join('\n');
}

/** A finished file holds exactly one row, and the row's ID is the file's name. */
export function finishedFileProblems(files: FinishedFile[]): string[] {
  const problems: string[] = [];
  for (const f of files) {
    const m = FINISHED_FILE_NAME.exec(f.name);
    if (!m) continue;
    const rows = rowLines(f.text);
    if (rows.length !== 1) {
      problems.push(`${ROADMAP_FINISHED_DIR}/${f.name} holds ${rows.length} rows; it holds one, the row for ${m[1]}.`);
      continue;
    }
    const id = /^\|\s*\*\*([A-Z]{1,3}-\d+)\*\*\s*\|/.exec(rows[0])?.[1];
    if (id !== m[1]) {
      problems.push(`${ROADMAP_FINISHED_DIR}/${f.name} holds the row for ${id ?? 'no ID'}; a file is named for the item its row is for.`);
    }
  }
  return problems;
}

const SPACED_ITEMS = /^(\s*-\s+\*\*[A-Z]{1,3}-\d+\b.*)\n(?:[ \t]*\n)+(?=\s*-\s+\*\*[A-Z]{1,3}-\d+\b)/gm;

/**
 * The roadmap with the blank lines between open items taken out, for the page.
 * The file keeps items a blank line apart so two pull requests changing
 * neighboring items merge (scripts/lib/roadmapFinishedCore.ts says why);
 * rendered as written, each item would be a spaced-out paragraph.
 */
export function withItemsTogether(markdown: string): string {
  return markdown.replace(SPACED_ITEMS, '$1\n');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * The roadmap with each Finished row's empty "Merged" cell filled from `dates`
 * (ID → YYYY-MM-DD, the day its file reached main), written like the rest:
 * "Oct 29". A cell that already holds a date is left as written.
 */
export function withMergedDates(markdown: string, dates: Record<string, string>): string {
  return markdown.replace(/^\|\s*\*\*([A-Z]{1,3}-\d+)\*\*\s*\|(.*)\|\s*\|\s*$/gm, (row, id: string, middle: string) => {
    const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dates[id] ?? '');
    if (!day || middle.split('|').length !== 2) return row;
    return `| **${id}** |${middle}| ${MONTHS[Number(day[2]) - 1]} ${Number(day[3])} |`;
  });
}
